import React, {useEffect, Suspense} from 'react';
// eslint-disable-next-line import/no-named-as-default
import i18n from 'i18next';
import HttpApi from 'i18next-http-backend';
import {I18nextProvider, useTranslation} from 'react-i18next';
import {Provider as StyleGuideProvider, Spinner} from '@te-digi/styleguide';
import {Locale} from '@shared/types/common';

export const COMMON_ID = 'VpCommon';
export const HP_COMMON_ID = 'HpCommon';
export const ERRORS_ID = 'errors';
export const HOUSEHOLD_ID = 'Household';

const initI18n = (localeApiPath: string, defaultLocale: string) => {
  const instance = i18n.createInstance();

  instance.use(HttpApi).init({
    debug: false,
    fallbackLng: defaultLocale,
    ns: [],
    interpolation: {
      escapeValue: false // not needed for react as it escapes by default
    },
    backend: {
      crossDomain: true,
      loadPath: `${localeApiPath}?locale={{lng}}&category=widgets&module={{ns}}`
    }
  });

  return instance;
};

type LocaleChangerProps = {
  locale: string;
  children: React.ReactNode;
  i18nContext: string | string[];
};

const LocaleChanger = ({locale, children, i18nContext}: LocaleChangerProps) => {
  const {i18n} = useTranslation(i18nContext);

  useEffect(() => {
    i18n.changeLanguage(locale);
  }, [i18n, locale]);

  return children;
};

type ProviderProps = {
  localeApiPath: string;
  locale: string;
  i18nContext: string | string[];
  children: React.ReactNode;
};

const I18nProvider = ({
  localeApiPath,
  locale,
  i18nContext,
  children
}: ProviderProps) => {
  const initedI18n = initI18n(localeApiPath, locale);

  return (
    <I18nextProvider i18n={initedI18n}>
      <StyleGuideProvider locale={locale as Locale}>
        <Suspense fallback={<Spinner />}>
          <LocaleChanger locale={locale} i18nContext={i18nContext}>
            {children}
          </LocaleChanger>
        </Suspense>
      </StyleGuideProvider>
    </I18nextProvider>
  );
};

export default I18nProvider;
